import { FC } from "react";
import {
  AlarmClockIcon,
  BellRingIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudOffIcon,
  CloudUploadIcon,
  FilePlusIcon,
  FileSpreadsheetIcon,
  ReceiptIcon,
  ThumbsUpIcon,
  TimerOffIcon,
  TruckIcon,
  XCircleIcon,
} from "lucide-react";
import {
  FeatureFlagType,
  SupplierNotificationSettingEvent,
} from "@/gql/graphql";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";

export type EventDef = {
  label: string;
  Icon: FC<{ className?: string }>;
  event: SupplierNotificationSettingEvent;
};

export type EventSection = {
  title: string;
  events: EventDef[];
};

export function useSupplierNotificationEvents(): EventSection[] {
  const acceptedEnabled = useFeatureFlag(FeatureFlagType.BookingAcceptedState);
  const rcmEnabled = useFeatureFlag(FeatureFlagType.RcmIntegration);
  const jucyEnabled = useFeatureFlag(FeatureFlagType.JucyIntegration);

  const sections: EventSection[] = [
    {
      title: "Booking Events",
      events: [
        {
          label: "Booking Pending",
          Icon: ClockIcon,
          event: SupplierNotificationSettingEvent.BookingPending,
        },
        ...(acceptedEnabled
          ? [
              {
                label: "Booking Accepted",
                Icon: ThumbsUpIcon,
                event: SupplierNotificationSettingEvent.BookingAccepted,
              },
            ]
          : []),
        {
          label: "Booking Confirmed",
          Icon: CheckCircleIcon,
          event: SupplierNotificationSettingEvent.BookingConfirmed,
        },
        {
          label: "Booking Cancelled (Before Confirmed)",
          Icon: TimerOffIcon,
          event:
            SupplierNotificationSettingEvent.BookingCancelledBeforeConfirmation,
        },
        {
          label: "Booking Cancelled (After Confirmed)",
          Icon: XCircleIcon,
          event:
            SupplierNotificationSettingEvent.BookingCancelledAfterConfirmation,
        },
        {
          label: "Pending Booking Reminder",
          Icon: BellRingIcon,
          event: SupplierNotificationSettingEvent.BookingPendingReminder,
        },
      ],
    },
    {
      title: "Relocation Events",
      events: [
        {
          label: "Relocation Created",
          Icon: FilePlusIcon,
          event: SupplierNotificationSettingEvent.RelocationsCreated,
        },
        {
          label: "Relocation Expired",
          Icon: TimerOffIcon,
          event: SupplierNotificationSettingEvent.RelocationsExpired,
        },
      ],
    },
    {
      title: "Reporting",
      events: [
        {
          label: "Monthly Booking Report",
          Icon: FileSpreadsheetIcon,
          event: SupplierNotificationSettingEvent.BookingReport,
        },
      ],
    },
    {
      title: "Invoice Events",
      events: [
        {
          label: "Invoice Issued",
          Icon: ReceiptIcon,
          event: SupplierNotificationSettingEvent.InvoiceIssued,
        },
        {
          label: "Invoice Overdue",
          Icon: AlarmClockIcon,
          event: SupplierNotificationSettingEvent.InvoiceOverdue,
        },
      ],
    },
    {
      title: "Pro Driver Events",
      events: [
        {
          label: "Pro Driver Quotes",
          Icon: TruckIcon,
          event: SupplierNotificationSettingEvent.ProDriverWorkOrderQuotes,
        },
      ],
    },
  ];

  const integrationEvents: EventDef[] = [
    ...(rcmEnabled
      ? [
          {
            label: "RCM push success",
            Icon: CloudUploadIcon,
            event: SupplierNotificationSettingEvent.RcmIntegrationPushSuccess,
          },
          {
            label: "RCM push failed",
            Icon: CloudOffIcon,
            event: SupplierNotificationSettingEvent.RcmIntegrationPushFailed,
          },
        ]
      : []),
    ...(jucyEnabled
      ? [
          {
            label: "Jucy push failed",
            Icon: CloudUploadIcon,
            event: SupplierNotificationSettingEvent.JucyIntegrationFailed,
          },
        ]
      : []),
  ];

  if (integrationEvents.length > 0) {
    sections.push({ title: "Integration Events", events: integrationEvents });
  }

  return sections;
}
